// PER-INSTANCE SIZE GATE: do not admit a nut that covers less than a pixel.
//
// WHAT ALREADY EXISTED, so this is not a second gate doing the same job:
// _shouldDefer tests a whole (tile, hash) GROUP against the pixel floors,
// using the group's CENTROID distance. Every instance in a group shares one
// fragment radius, so the only thing that can differ between them is
// distance - and that is bounded by how far the group spreads.
//
// MEASURED ON THE LIVE SpaceX EXPORT (200 tiles, 22,725 groups, 112,281
// rows) before writing any of this, because by GROUPS it looks pointless:
//     spread < 1% of the cull distance   84.1% of groups   22.9% of INSTANCES
//     1-10%                               9.1%             16.4%
//     10-50%                              3.8%              7.6%
//     over 50%                            3.0%             53.1%
// Counting groups says the centroid is a fine proxy. Counting INSTANCES -
// which is what the GPU and the main thread actually pay for - says the
// opposite: over half of them sit in groups spread wider than half their own
// cull distance, because the groups with many instances are exactly the ones
// that fill their tile. The unit of measurement decided the answer.
//
// NOTHING IS DROPPED. Skipped instances go back to the caller to be held in
// tile._deferredHashes, which the rest-window revive already re-checks and
// materialises when the camera comes close. That is the same promise the
// group gate makes, and it is why this is a deferral and not a filter: we
// spent today removing a dedupe that silently discarded 82% of a model, and
// this must not be a new one wearing a different hat.

import { Vector3 } from 'three';

const _v = new Vector3();

// The translation of an instance row, in world space.
//
// THE LAYOUT IS NOT A CONSTANT. JSON sidecar rows are C# row-major, with the
// translation at 3/7/11; binary sidecar rows (sidecarFormat 2) and moved rows
// are column-major, at 12/13/14, and carry `cm`. Reading the wrong three is
// exactly the bug that made this model look 82% empty for eighteen days - it
// reads as 0,0,0, which is a plausible position rather than an obvious fault.
export function instanceWorldPosition(inst, rootMatrix, out = _v) {
  const m = inst && inst.deltaMatrix;
  if (!m || m.length < 16) return null;
  if (inst.cm) out.set(m[12], m[13], m[14]);
  else out.set(m[3], m[7], m[11]);
  if (rootMatrix) out.applyMatrix4(rootMatrix);
  return out;
}

// px an instance covers: the same formula _pxOf uses, per instance.
export function pixelsFor(radius, distance, focalPx) {
  if (!(radius > 0) || !(focalPx > 0)) return null;
  return (radius * 2 * focalPx) / Math.max(distance, 1);
}

/**
 * Split a group's instances into the ones worth drawing and the ones to hold.
 *
 * Returns { admit, skip }. `admit` is the SAME ARRAY OBJECT when nothing is
 * skipped, so the common case costs nothing and the caller's existing
 * fast paths are untouched.
 */
export function splitByPixelSize(instances, opts = {}) {
  const {
    radius, focalPx, minPx = 2, cameraPos, rootMatrix = null,
    // A deliberate act outranks a derived one: anything the page has pinned,
    // isolated or put in focus is drawn whatever its size. The caller passes
    // the same predicate the rest of the viewer uses rather than this module
    // inventing a second opinion about what is important.
    keep = null,
  } = opts;
  const rows = Array.isArray(instances) ? instances : [];
  // No radius, no camera, no floor -> no opinion. Admit everything; a gate
  // that cannot measure must not guess.
  if (!rows.length || !(radius > 0) || !(minPx > 0) || !cameraPos || !(focalPx > 0)) {
    return { admit: rows, skip: [] };
  }
  // The distance at which this fragment falls under the floor. Computed once
  // per group: px = r*2*focal/d, so px < minPx exactly when d > r*2*focal/minPx.
  const cullBeyond = (radius * 2 * focalPx) / minPx;
  const cull2 = cullBeyond * cullBeyond;

  let skip = null;
  let admit = null;
  for (let i = 0; i < rows.length; i++) {
    const inst = rows[i];
    const p = instanceWorldPosition(inst, rootMatrix);
    // An unreadable matrix is not a small instance. Admit it and let the
    // rest of the pipeline deal with it.
    const far = p ? (p.distanceToSquared(cameraPos) > cull2) : false;
    const held = far && !(keep && keep(inst));
    if (!held) { if (admit) admit.push(inst); continue; }
    if (!skip) {
      skip = [];
      admit = rows.slice(0, i);   // only now does it cost anything
    }
    skip.push(inst);
  }
  return skip ? { admit, skip } : { admit: rows, skip: [] };
}
